// 📄 components/proyecto/SolutionStatsView.tsx
// Vista del ProjectStatsPanel para una solución específica (Commerce, SLP,
// Sourcing, Contracts...): misma estructura visual que GeneralStatsView
// (dona + metric cards + barras), pero con datos filtrados por solución.
import React from 'react';
import { Ticket, AlertTriangle, Layers, Settings } from 'lucide-react';
import DonutChart from '../common/DonutChart.tsx';
import MetricCard from '../common/MetricCard.tsx';
import ProgressRow from '../common/ProgressRow.tsx';
import { useSolutionStats } from '../../hooks/useSolutionStats.ts';

interface SolutionStatsViewProps {
  solutionId: string;
  solutionLabel: string;
  projectId: string;
  onBack?: () => void;
}

const COLOR_COMPLETADO = '#2563eb';
const COLOR_PENDIENTE = '#4b5563';

export default function SolutionStatsView({ solutionId, solutionLabel, projectId, onBack }: SolutionStatsViewProps) {
  const {
    loading,
    error,
    disponible,
    totalPasos,
    pasosCompletados,
    porcentaje,
    canales,
    canalesConfigurados,
    totalCanales,
    srCount,
    issuesCount
  } = useSolutionStats(projectId, solutionId);

  if (loading) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-[auto_1fr] gap-6 animate-pulse">
        <div className="w-[140px] h-[140px] rounded-full bg-white/10 mx-auto lg:mx-0" />
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="h-20 bg-white/10 rounded-xl" />
            <div className="h-20 bg-white/10 rounded-xl" />
            <div className="h-20 bg-white/10 rounded-xl" />
          </div>
          <div className="h-24 bg-white/10 rounded-xl" />
        </div>
      </div>
    );
  }

  if (error) {
    return <div className="text-sm text-gray-500 text-center py-6">{error}</div>;
  }

  if (!disponible) {
    return (
      <div className="text-center py-8 text-gray-400">
        <Settings className="w-8 h-8 mx-auto mb-2" />
        <p className="text-sm">{solutionLabel} aún no tiene checklist de integración configurado</p>
        <p className="text-xs mt-1 text-gray-500">No hay canales de integración definidos todavía para esta solución.</p>
        {onBack && (
          <button onClick={onBack} className="text-xs text-blue-400 hover:underline mt-4">
            ← Volver a vista general
          </button>
        )}
      </div>
    );
  }

  const pasosPendientes = totalPasos - pasosCompletados;
  const segments = [
    { label: 'Completado', value: pasosCompletados, color: COLOR_COMPLETADO },
    { label: 'Pendiente', value: pasosPendientes, color: COLOR_PENDIENTE }
  ];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 lg:grid-cols-[auto_1fr] gap-6">
        {/* Columna izquierda: dona */}
        <div className="flex flex-col items-center lg:items-start gap-3">
          <p className="text-sm text-gray-400">Progreso de {solutionLabel}</p>
          <DonutChart
            segments={segments}
            centerLabel={`${porcentaje}%`}
            centerSublabel={totalPasos > 0 ? `${pasosCompletados} de ${totalPasos}` : 'Sin pasos'}
          />
          <div className="flex flex-col gap-1 text-xs text-gray-400">
            {segments.map((s) => (
              <span key={s.label} className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: s.color }} />
                {s.label} {s.value}
              </span>
            ))}
          </div>
        </div>

        {/* Columna derecha: métricas + avance por canal */}
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <MetricCard icon={Ticket} label="SR creadas" value={srCount} />
            <MetricCard icon={AlertTriangle} label="Issues abiertos" value={issuesCount} variant="danger" />
            <MetricCard icon={Layers} label="Canales configurados" value={`${canalesConfigurados}/${totalCanales}`} />
          </div>

          <div className="bg-white/5 border border-white/10 rounded-xl p-4 space-y-3">
            <p className="text-sm text-gray-400">Avance por canal · {solutionLabel}</p>
            {canales.map((canal) => (
              <ProgressRow
                key={canal.id}
                label={canal.nombre}
                completed={canal.completados}
                total={canal.total}
                showStatusIcon
              />
            ))}
          </div>
        </div>
      </div>

      {onBack && (
        <button onClick={onBack} className="text-xs text-blue-400 hover:underline">
          ← Volver a vista general
        </button>
      )}
    </div>
  );
}
